import FlexSearch from 'flexsearch';
import { getSearchIndex } from './contentService';
import { logError } from '../utils/errorHandler';

let searchIndex = null;
let documentsMap = new Map();
let initialized = false;

/**
 * Initializes the search index with data
 */
export async function initializeSearch() {
  if (initialized) {
    return;
  }

  try {
    const indexData = await getSearchIndex();

    // Create FlexSearch index
    searchIndex = new FlexSearch.Index({
      tokenize: 'forward',
      optimize: true,
      resolution: 9,
      context: {
        depth: 3,
        resolution: 9
      }
    });

    // Add documents to index
    indexData.documents.forEach(doc => {
      // Store document in map for retrieval
      documentsMap.set(doc.id, doc);

      // Index title and content
      const searchText = `${doc.title} ${doc.content}`;
      searchIndex.add(doc.id, searchText);
    });

    initialized = true;
    console.log(`Search index initialized with ${indexData.documents.length} documents`);
  } catch (error) {
    logError('searchService.initializeSearch', error);
    throw error;
  }
}

/**
 * Searches for documents matching the query
 * @param {string} query - Search query
 * @param {Object} filters - Optional filters (tags, categories)
 * @returns {Array} Array of matching documents with highlights
 */
export async function search(query, filters = {}) {
  if (!initialized) {
    await initializeSearch();
  }

  if (!query || query.trim().length === 0) {
    return [];
  }

  try {
    // Perform search
    const resultIds = searchIndex.search(query, {
      limit: 50,
      suggest: true
    });

    // Get full documents for results
    let results = resultIds
      .map(id => documentsMap.get(id))
      .filter(Boolean);

    // Apply filters
    if (filters.tags && filters.tags.length > 0) {
      results = results.filter(doc =>
        doc.tags.some(tag =>
          filters.tags.includes(tag.toLowerCase())
        )
      );
    }

    if (filters.categories && filters.categories.length > 0) {
      results = results.filter(doc =>
        doc.categories.some(category =>
          filters.categories.includes(category.toLowerCase())
        )
      );
    }

    // Add highlights
    results = results.map(doc => ({
      ...doc,
      highlight: generateHighlight(doc, query)
    }));

    return results;
  } catch (error) {
    logError('searchService.search', error, { query, filters });
    return [];
  }
}

/**
 * Generates a highlighted excerpt containing the search query
 */
function generateHighlight(doc, query) {
  const lowerQuery = query.toLowerCase();
  const lowerContent = doc.content.toLowerCase();

  // Find the position of the query in content
  const index = lowerContent.indexOf(lowerQuery);

  if (index === -1) {
    // Query not found in content, use excerpt
    return doc.excerpt;
  }

  // Extract context around the match
  const start = Math.max(0, index - 60);
  const end = Math.min(doc.content.length, index + query.length + 60);

  let excerpt = doc.content.substring(start, end);

  // Add ellipsis if needed
  if (start > 0) excerpt = '...' + excerpt;
  if (end < doc.content.length) excerpt = excerpt + '...';

  // Highlight the query (simple replacement)
  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
  excerpt = excerpt.replace(regex, '<mark>$1</mark>');

  return excerpt;
}

/**
 * Escapes special regex characters
 */
function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Gets search suggestions based on partial query
 * @param {string} query - Partial search query
 * @returns {Array} Array of suggested terms
 */
export async function getSuggestions(query) {
  if (!initialized) {
    await initializeSearch();
  }

  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    // Get partial matches
    const resultIds = searchIndex.search(query, {
      limit: 5,
      suggest: true
    });

    // Extract titles as suggestions
    return resultIds
      .map(id => {
        const doc = documentsMap.get(id);
        return doc ? doc.title : null;
      })
      .filter(Boolean);
  } catch (error) {
    logError('searchService.getSuggestions', error, { query });
    return [];
  }
}

/**
 * Clears the search index (useful for development)
 */
export function clearSearchIndex() {
  searchIndex = null;
  documentsMap.clear();
  initialized = false;
}
